import { useCallback, useMemo, useState, useRef, SyntheticEvent } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { SearchInput } from '../SearchInput';
import SweepSelect from '../SweepSelect';
import { colors } from '@sweep-io/sweep-design';
import StyledTooltip from '../StyledTooltip';
import keyBy from 'lodash/keyBy';
import { Box, Fade, MenuItem, SelectProps, styled } from '@mui/material';
import { SearchSelectListSubheader } from './SearchSelectListSubheader';
import { VirtualizedSearchSelectProps, SearchSelectItem } from './selectTypes';
import {
  defaultItemRenderer,
  DEFAULT_EMPTY_RESULTS_MESSAGE,
  maybeRenderEmptyResults,
} from './selectUtils';

const DEFAULT_ITEM_HEIGHT = 36; // Default height of a MenuItem
const DEFAULT_MAX_HEIGHT = 300; // Default max height of the dropdown
const SEARCH_COMPONENT_HEIGHT = 70;

const VirtualizedList = styled('div')({
  width: '100%',
  '& .virtuoso-scroller': {
    scrollbarWidth: 'thin',
    '&::-webkit-scrollbar': {
      width: '6px',
    },
    '&::-webkit-scrollbar-track': {
      background: colors.grey[100],
      borderRadius: '3px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: colors.grey[300],
      borderRadius: '3px',
      '&:hover': {
        background: colors.grey[400],
      },
    },
  },
});

// Re-export types for convenience
export type { SearchSelectItem, VirtualizedSearchSelectProps } from './selectTypes';

export function VirtualizedSearchSelect<T = any>({
  items,
  valueRenderer,
  onChange,
  searchPlaceholder,
  children,
  itemRenderer = defaultItemRenderer,
  emptyResultsMessage = DEFAULT_EMPTY_RESULTS_MESSAGE,
  itemHeight = DEFAULT_ITEM_HEIGHT,
  maxHeight = DEFAULT_MAX_HEIGHT,
  ...sweepSelectProps
}: VirtualizedSearchSelectProps<T>) {
  const [search, setSearch] = useState('');
  // Local isOpen state is required for proper virtuoso component behavior and menu closing after selection
  const [isOpen, setIsOpen] = useState(!!sweepSelectProps.autoOpen);
  const itemsByKey = useMemo(() => keyBy(items, 'value'), [items]);
  const virtuosoRef = useRef<any>(null);

  const localItems = useMemo(() => {
    return items.filter((item) => {
      return (
        sweepSelectProps.SelectProps?.value === item.value ||
        item.label.toUpperCase().includes(search.toUpperCase())
      );
    });
  }, [items, sweepSelectProps.SelectProps?.value, search]);

  const selectedItemIndex = useMemo(() => {
    return localItems.findIndex((item) => item.value === sweepSelectProps.SelectProps?.value);
  }, [localItems, sweepSelectProps.SelectProps?.value]);

  const selectProps: SelectProps = useMemo(() => {
    return {
      ...sweepSelectProps.SelectProps,
      open: isOpen,
      onOpen: (event: SyntheticEvent) => {
        setIsOpen(true);
        sweepSelectProps.SelectProps?.onOpen?.(event);
      },
      onClose: (event: SyntheticEvent) => {
        setIsOpen(false);
        sweepSelectProps.SelectProps?.onClose?.(event);
      },
      onChange: (event) => {
        const item = itemsByKey[event.target.value as string];
        item && onChange && onChange(item);
      },
      inputProps: {
        ...sweepSelectProps.SelectProps?.inputProps,
        onChange: (event) => {
          sweepSelectProps.SelectProps?.inputProps?.onChange &&
            sweepSelectProps.SelectProps?.inputProps?.onChange(event);
        },
      },
    };
  }, [itemsByKey, onChange, isOpen, sweepSelectProps.SelectProps]);

  const renderValue = useCallback(
    (value: unknown) => {
      const item = items.find((item) => item.value === value);
      if (valueRenderer) {
        return valueRenderer(item as SearchSelectItem<T>);
      }
      return <>{item?.label || (value as any)}</>;
    },
    [items, valueRenderer],
  );

  const handleItemClick = useCallback(
    (item: SearchSelectItem<T>) => {
      if (item.disabled) return;

      // Create a synthetic event to match the Select's onChange signature
      const syntheticEvent = {
        target: { value: item.value },
      } as any;

      // Call the SelectProps onChange handler
      if (selectProps.onChange) {
        selectProps.onChange(syntheticEvent, undefined as any);
      }

      setIsOpen(false);
    },
    [selectProps],
  );

  const renderItem = useCallback(
    (index: number) => {
      const item = localItems[index];

      if (!item) return null;

      return (
        <MenuItem
          key={item.value}
          value={item.value}
          selected={item.value === sweepSelectProps.SelectProps?.value}
          disabled={item.disabled}
          onClick={() => handleItemClick(item)}
          sx={{
            height: itemHeight,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <StyledTooltip
            title={item.tooltip}
            PopperProps={{
              sx: {
                zIndex: 999999, // Needs to be higher than the dropdown of the select
              },
            }}
            placement="right"
          >
            <Box sx={{ width: '100%' }}>
              {itemRenderer ? itemRenderer(item, search) : item.label}
            </Box>
          </StyledTooltip>
        </MenuItem>
      );
    },
    [
      localItems,
      itemHeight,
      search,
      itemRenderer,
      sweepSelectProps.SelectProps?.value,
      handleItemClick,
    ],
  );

  return (
    <SweepSelect
      {...sweepSelectProps}
      MenuProps={{
        ...sweepSelectProps.MenuProps,
        PaperProps: { style: { overflow: 'hidden' } },
        autoFocus: false,
        TransitionComponent: Fade, // hack: only this transition render items early enough for virtuoso to make correct calculations
        sx: {
          '.MuiList-root': {
            paddingTop: 0,
            paddingBottom: 0,
          },
        },
      }}
      menuMaxHeight={`${maxHeight}px`}
      SelectProps={selectProps}
      renderValue={renderValue}
      onOpenCb={(event) => {
        setSearch('');
        setIsOpen(true);
        sweepSelectProps.onOpenCb && sweepSelectProps.onOpenCb(event);
      }}
      onCloseCb={() => {
        setIsOpen(false);
        sweepSelectProps.onCloseCb && sweepSelectProps.onCloseCb();
      }}
    >
      {!Boolean(sweepSelectProps.isLoading) && !children && (
        <SearchSelectListSubheader sx={{ height: SEARCH_COMPONENT_HEIGHT }}>
          <SearchInput
            withFixedMagnifyingGlassIcon
            placeholder={searchPlaceholder}
            TextFieldProps={{
              sx: { minWidth: '270px' },
              value: search,
              autoFocus: true,
              onChange: (event) => {
                const value = event.target.value;
                setSearch(value);
              },
              onKeyDown: (e) => {
                if (e.key !== 'Escape') {
                  // Prevents autoselecting item while typing (default Select behaviour)
                  e.stopPropagation();
                }
              },
              onClick: (e) => {
                e.stopPropagation();
              },
            }}
          />
        </SearchSelectListSubheader>
      )}
      {maybeRenderEmptyResults(emptyResultsMessage, localItems.length > 0)}
      {!children && <MenuItem value="" hidden />}
      {!children && localItems.length > 0 && (
        <VirtualizedList>
          <Virtuoso
            ref={virtuosoRef}
            totalCount={localItems.length}
            itemContent={renderItem}
            initialTopMostItemIndex={selectedItemIndex !== -1 ? selectedItemIndex : 0}
            style={{
              height: Math.min(maxHeight - SEARCH_COMPONENT_HEIGHT, localItems.length * itemHeight),
            }}
            components={{
              Item: ({ children, ...props }) => (
                <div {...props} style={{ ...props.style, padding: 0, marginBottom: '8px' }}>
                  {children}
                </div>
              ),
            }}
          />
        </VirtualizedList>
      )}
      {children}
    </SweepSelect>
  );
}
